import { useCallback, useMemo } from 'react';

import { useSearchPageData } from '#src/app/pages/search/pageContext';
import {
	INSTANT_SEARCH_CACHE_SECONDS,
	INSTANT_SEARCH_LOCATION_COUNTRY_CODE_FILTER,
	INSTANT_SEARCH_LOCATION_FILTER_MAX_ITEMS,
} from '#src/app/pages/search/shared/consts';
import type { LocationItem } from '#src/app/pages/search/shared/types';

import { useInstantSearchIndexName } from '../../../../../useInstantSearchIndexName';
import { useSearchClient } from '../../../../../useSearchClient';

type Result = ModelHookResult<
	undefined,
	{
		searchForRelevantCountries: (queryLocation: string, selectedLocation: string) => Promise<readonly LocationItem[]>;
	}
>;

export function useSearchForRelevantCountries(): Result {
	const {
		data: { typesenseClient },
	} = useSearchClient();

	const {
		data: { instantSearchIndexName },
	} = useInstantSearchIndexName();

	const {
		data: { countries: pageCountries },
	} = useSearchPageData();

	const countryDisplayNameMap = useMemo(
		() => Object.fromEntries(pageCountries.map((c) => [c.countryCode, c.displayName])),
		[pageCountries],
	);

	return {
		actions: {
			searchForRelevantCountries: useCallback(
				async (queryLocation: string, selectedLocation: string) => {
					const response = await typesenseClient
						.collections(instantSearchIndexName)
						.documents()
						.search(
							{
								q: '*',
								query_by: '*',
								facet_by: INSTANT_SEARCH_LOCATION_COUNTRY_CODE_FILTER,
								facet_query: `${INSTANT_SEARCH_LOCATION_COUNTRY_CODE_FILTER}:${queryLocation}`,
								max_facet_values: INSTANT_SEARCH_LOCATION_FILTER_MAX_ITEMS,
							},
							{
								cacheSearchResultsForSeconds: INSTANT_SEARCH_CACHE_SECONDS,
							},
						);
					const facetCount = response.facet_counts?.find(
						(facet) => facet.field_name === INSTANT_SEARCH_LOCATION_COUNTRY_CODE_FILTER,
					);
					const countries = facetCount?.counts
						? facetCount.counts.map(({ count, highlighted, value }) => ({
								label: countryDisplayNameMap[value] || value,
								value,
								highlighted,
								count,
								isRefined: selectedLocation === value,
								type: 'country' as const,
							}))
						: [];
					return countries;
				},
				[countryDisplayNameMap, instantSearchIndexName, typesenseClient],
			),
		},
	};
}
